import { useState, useCallback, useMemo, useRef } from 'react';
import { Pencil, RotateCcw, X, Plus, Minus, Maximize2, Save, Trash2, Check } from 'lucide-react';
import { PondShape } from './PondShape';
import { getPondStatus } from './pondStatus';
import { PondTooltip } from './PondTooltip';
import { usePondLayout } from '@/hooks/usePondLayout';
import { useMembership } from '@/hooks/useMembership';
import type { PondOverview } from '@/types/api';
import type { StatusColor } from './pondStatus';

interface FarmBlueprintMapProps {
  ponds: PondOverview[];
  onPondClick: (pond: PondOverview) => void;
  farmId: string;
}

interface TooltipState {
  pond: PondOverview;
  status: StatusColor;
  screenX: number;
  screenY: number;
}

type DragMode = 'move' | 'resize' | 'pan';

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;

export function FarmBlueprintMap({
  ponds,
  onPondClick,
  farmId,
}: FarmBlueprintMapProps) {
  const { isSupervisorOrAbove } = useMembership();
  const [isEditMode, setIsEditMode] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragMode, setDragMode] = useState<DragMode>('move');
  const [isPanning, setIsPanning] = useState(false);

  // Zoom/pan: we control the viewBox origin + scale
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [zoomInputValue, setZoomInputValue] = useState<string | null>(null); // null = not editing
  const [showSaveToast, setShowSaveToast] = useState(false);
  const saveToastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const dragStart = useRef({ x: 0, y: 0, origW: 0, origH: 0 });
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { positions, updatePosition, updateSize, saveLayout, resetLayout, factoryReset, hasSavedDefault, viewBox } = usePondLayout(farmId, ponds);
  const VW = viewBox.w;
  const VH = viewBox.h;

  // Computed viewBox with zoom/pan applied
  const zoomedVBW = VW / zoom;
  const zoomedVBH = VH / zoom;
  const vbX = panOffset.x;
  const vbY = panOffset.y;

  // Issue #115: memoize derived lists so re-renders from tooltip / drag /
  // pan state don't re-sort on every paint. On low-end Android the cost of
  // re-sorting and recomputing dike paths compounds with the heavy SVG
  // terrain filter.
  const sortedPonds = useMemo(
    () => [...ponds].sort((a, b) => a.name.localeCompare(b.name)),
    [ponds]
  );
  const activePonds = ponds.filter((p) => p.status === 'active');
  const preparingPonds = ponds.filter((p) => p.status === 'preparing');
  const idlePonds = ponds.filter((p) => p.status !== 'active' && p.status !== 'preparing');
  const alertCount = ponds.reduce((sum, p) => sum + (p.alert_count ?? 0), 0);

  const screenToSvg = useCallback(
    (clientX: number, clientY: number) => {
      if (!svgRef.current) return { x: 0, y: 0 };
      const pt = svgRef.current.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const svgPt = pt.matrixTransform(svgRef.current.getScreenCTM()?.inverse());
      return { x: svgPt.x, y: svgPt.y };
    },
    [],
  );

  const svgToScreen = useCallback(
    (svgX: number, svgY: number) => {
      if (!svgRef.current || !containerRef.current) return { x: 0, y: 0 };
      const pt = svgRef.current.createSVGPoint();
      pt.x = svgX;
      pt.y = svgY;
      const screenPt = pt.matrixTransform(svgRef.current.getScreenCTM()!);
      const rect = containerRef.current.getBoundingClientRect();
      return { x: screenPt.x - rect.left, y: screenPt.y - rect.top };
    },
    [],
  );

  // Zoom towards mouse position
  const zoomAt = useCallback(
    (clientX: number, clientY: number, newZoom: number) => {
      const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));
      if (!svgRef.current) { setZoom(clamped); return; }

      // Get SVG coords under cursor before zoom
      const svgPt = screenToSvg(clientX, clientY);

      // After zoom, the same screen point should map to the same SVG coord
      // newPanX + (screenFraction * newVBW) = svgPt.x
      // screenFraction = (clientX - svgRect.left) / svgRect.width
      const svgRect = svgRef.current.getBoundingClientRect();
      const fracX = (clientX - svgRect.left) / svgRect.width;
      const fracY = (clientY - svgRect.top) / svgRect.height;
      const newVBW = VW / clamped;
      const newVBH = VH / clamped;

      setPanOffset({
        x: svgPt.x - fracX * newVBW,
        y: svgPt.y - fracY * newVBH,
      });
      setZoom(clamped);
    },
    [VW, VH, screenToSvg],
  );

  // No wheel/trackpad zoom — only zoom via buttons

  // Fit-to-view: reset zoom/pan
  const fitToView = useCallback(() => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, zoom + 0.1);
    setTooltip(null);
  }, [zoom, zoomAt]);

  const zoomOut = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, zoom - 0.1);
    setTooltip(null);
  }, [zoom, zoomAt]);

  // Hover — compute screen coords immediately so we don't access refs during render
  const handleHoverStart = useCallback(
    (pond: PondOverview, svgX: number, svgY: number) => {
      if (draggingId || isPanning || isEditMode) return;
      const screen = svgToScreen(svgX, svgY);
      setTooltip({ pond, status: getPondStatus(pond), screenX: screen.x, screenY: screen.y });
    },
    [draggingId, isPanning, isEditMode, svgToScreen],
  );
  const handleHoverEnd = useCallback(() => setTooltip(null), []);

  // Pond move drag
  const handleDragStart = useCallback(
    (pondId: string, e: React.PointerEvent) => {
      if (!isEditMode) return;
      const svgCoords = screenToSvg(e.clientX, e.clientY);
      const pos = positions[pondId];
      if (!pos) return;
      dragStart.current = { x: svgCoords.x - pos.x, y: svgCoords.y - pos.y, origW: 0, origH: 0 };
      setDraggingId(pondId);
      setDragMode('move');
      setTooltip(null);
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [isEditMode, positions, screenToSvg],
  );

  // Pond resize drag
  const handleResizeStart = useCallback(
    (pondId: string, e: React.PointerEvent) => {
      if (!isEditMode) return;
      const svgCoords = screenToSvg(e.clientX, e.clientY);
      const pos = positions[pondId];
      if (!pos) return;
      dragStart.current = { x: svgCoords.x, y: svgCoords.y, origW: pos.w, origH: pos.h };
      setDraggingId(pondId);
      setDragMode('resize');
      setTooltip(null);
      (e.target as Element).setPointerCapture?.(e.pointerId);
    },
    [isEditMode, positions, screenToSvg],
  );

  // Pan start — triggered on background pointerdown (not on a pond)
  const handleBgPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Only start pan if clicking on background, not on an interactive pond element
      const target = e.target as Element;
      if (target.closest('[data-pond]')) return;
      panStart.current = { x: e.clientX, y: e.clientY, panX: panOffset.x, panY: panOffset.y };
      setIsPanning(true);
      setTooltip(null);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    },
    [panOffset],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      // Panning the canvas
      if (isPanning) {
        if (!svgRef.current) return;
        const svgRect = svgRef.current.getBoundingClientRect();
        // Convert screen pixel delta to SVG units
        const scaleX = zoomedVBW / svgRect.width;
        const scaleY = zoomedVBH / svgRect.height;
        const dx = (e.clientX - panStart.current.x) * scaleX;
        const dy = (e.clientY - panStart.current.y) * scaleY;
        setPanOffset({
          x: panStart.current.panX - dx,
          y: panStart.current.panY - dy,
        });
        return;
      }

      if (!draggingId) return;
      const svgCoords = screenToSvg(e.clientX, e.clientY);
      const pos = positions[draggingId];
      if (!pos) return;

      if (dragMode === 'move') {
        const newX = svgCoords.x - dragStart.current.x;
        const newY = svgCoords.y - dragStart.current.y;
        updatePosition(draggingId, newX, newY);
      } else if (dragMode === 'resize') {
        const dx = svgCoords.x - dragStart.current.x;
        const dy = svgCoords.y - dragStart.current.y;
        updateSize(draggingId, dragStart.current.origW + dx, dragStart.current.origH + dy);
      }
    },
    [isPanning, draggingId, dragMode, screenToSvg, updatePosition, updateSize, positions, zoomedVBW, zoomedVBH],
  );

  const handlePointerUp = useCallback(() => {
    setDraggingId(null);
    setIsPanning(false);
  }, []);


  const zoomPct = Math.round(zoom * 100);

  return (
    <div className="space-y-4">
      <div ref={containerRef} className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        {/* Edit toolbar (top-right): layout editing is supervisor+ */}
        {isSupervisorOrAbove && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
          {isEditMode ? (
            <>
              <button
                onClick={() => {
                  saveLayout();
                  setShowSaveToast(true);
                  clearTimeout(saveToastTimer.current);
                  saveToastTimer.current = setTimeout(() => setShowSaveToast(false), 2000);
                }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600/90 hover:bg-primary-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors"
                title="Save current layout as your default"
              >
                <Save className="h-3.5 w-3.5" />
                Save Layout
              </button>
              <button
                onClick={resetLayout}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white/90 hover:bg-white rounded-lg shadow-sm backdrop-blur-sm transition-colors"
                title={hasSavedDefault ? 'Reset to your saved layout' : 'Reset to default grid'}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </button>
              <button
                onClick={factoryReset}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-700 bg-red-50/90 hover:bg-red-100 rounded-lg shadow-sm backdrop-blur-sm transition-colors"
                title="Reset to factory default grid layout"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Factory Reset
              </button>
              <button
                onClick={() => { setIsEditMode(false); setTooltip(null); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-green-600/90 hover:bg-green-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Done
              </button>
            </>
          ) : (
            <button
              onClick={() => { setIsEditMode(true); setTooltip(null); }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white/90 hover:bg-white rounded-lg shadow-sm backdrop-blur-sm transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit Layout
            </button>
          )}
        </div>
        )}

        {isEditMode && (
          <div className="absolute top-3 left-3 z-10 px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100/90 rounded-lg backdrop-blur-sm shadow-sm">
            Drag to move, corner handle to resize
          </div>
        )}

        {/* Save confirmation toast */}
        {showSaveToast && (
          <div className="absolute top-14 right-3 z-20 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-800 bg-green-100/95 rounded-lg backdrop-blur-sm shadow-sm animate-fade-in">
            <Check className="h-3.5 w-3.5" />
            Layout saved!
          </div>
        )}

        {/* Zoom controls (top-left, below edit-mode banner) */}
        <div className={`absolute ${isEditMode ? 'top-14' : 'top-3'} left-3 z-10 flex flex-col items-center gap-1`}>
          <button
            onClick={zoomIn}
            className="w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white rounded-lg shadow-sm backdrop-blur-sm transition-colors text-gray-700"
            title="Zoom in"
          >
            <Plus className="h-4 w-4" />
          </button>
          <input
            type="text"
            value={zoomInputValue ?? `${zoomPct}%`}
            onFocus={(e) => {
              setZoomInputValue(String(zoomPct));
              requestAnimationFrame(() => e.target.select());
            }}
            onChange={(e) => setZoomInputValue(e.target.value)}
            onBlur={() => {
              if (zoomInputValue != null) {
                const num = parseInt(zoomInputValue.replace(/[^0-9]/g, ''), 10);
                if (!isNaN(num) && num > 0) {
                  const clamped = Math.max(Math.round(MIN_ZOOM * 100), Math.min(Math.round(MAX_ZOOM * 100), num));
                  setZoom(clamped / 100);
                }
              }
              setZoomInputValue(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                setZoomInputValue(null);
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="w-12 text-center text-[10px] font-medium text-gray-600 bg-white/80 hover:bg-white/95 focus:bg-white rounded px-1 py-0.5 backdrop-blur-sm tabular-nums outline-none focus:ring-1 focus:ring-primary-400 cursor-text"
          />
          <button
            onClick={zoomOut}
            className="w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white rounded-lg shadow-sm backdrop-blur-sm transition-colors text-gray-700"
            title="Zoom out"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button
            onClick={fitToView}
            className="w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white rounded-lg shadow-sm backdrop-blur-sm transition-colors text-gray-700 mt-1"
            title="Fit to view"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* SVG Canvas */}
        <svg
          ref={svgRef}
          viewBox={`${vbX} ${vbY} ${zoomedVBW} ${zoomedVBH}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          className="block"
          style={{ cursor: isPanning ? 'grabbing' : (isEditMode ? 'default' : 'grab') }}
          onPointerDown={handleBgPointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <defs>
            {/* Earth terrain gradient */}
            <linearGradient id="terrain" x1="0" y1="0" x2="0.5" y2="1">
              <stop offset="0%" stopColor="#8B9E6B" />
              <stop offset="30%" stopColor="#7A8E5C" />
              <stop offset="60%" stopColor="#6D7F52" />
              <stop offset="100%" stopColor="#5C6E45" />
            </linearGradient>

            {/* Terrain texture */}
            <filter id="terrain-noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" result="noise" />
              <feColorMatrix type="saturate" values="0" in="noise" result="grayNoise" />
              <feBlend in="SourceGraphic" in2="grayNoise" mode="multiply" result="blended" />
              <feComponentTransfer in="blended">
                <feFuncA type="linear" slope="1" />
              </feComponentTransfer>
            </filter>

            {/* Dike path fill */}
            <linearGradient id="dike-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A0926B" />
              <stop offset="100%" stopColor="#8B7D5A" />
            </linearGradient>
          </defs>

          {/* Earth terrain background — extend well beyond viewBox for pan */}
          <rect x={-500} y={-500} width={VW + 1000} height={VH + 1000} fill="url(#terrain)" filter="url(#terrain-noise)" />

          {/* Farm boundary */}
          <rect
            x="20" y="20"
            width={VW - 40} height={VH - 40}
            rx="12" ry="12"
            fill="none"
            stroke="#4A5A38"
            strokeWidth="3"
            strokeDasharray="8 4"
            opacity="0.5"
          />

          {/* Vegetation patches */}
          <VegetationPatches vw={VW} vh={VH} />

          {/* Dike paths */}
          <DikePaths positions={positions} ponds={sortedPonds} />

          {/* Ponds */}
          {sortedPonds.map((pond) => {
            const pos = positions[pond.id];
            if (!pos) return null;
            return (
              <PondShape
                key={pond.id}
                pond={pond}
                x={pos.x}
                y={pos.y}
                w={pos.w}
                h={pos.h}
                status={getPondStatus(pond)}
                isEditMode={isEditMode}
                isHovered={tooltip?.pond.id === pond.id}
                onHoverStart={handleHoverStart}
                onHoverEnd={handleHoverEnd}
                onClick={onPondClick}
                onDragStart={handleDragStart}
                onResizeStart={handleResizeStart}
              />
            );
          })}

          {ponds.length === 0 && (
            <text
              x={VW / 2} y={VH / 2}
              textAnchor="middle" dominantBaseline="central"
              fill="#3D4A30" fontSize="16" fontWeight="500"
            >
              {isSupervisorOrAbove
                ? 'No ponds yet. Add ponds from the Ponds page.'
                : 'No ponds yet. A Farm Technician adds ponds.'}
            </text>
          )}
        </svg>

        {/* Tooltip */}
        {tooltip && !isPanning && (
          <PondTooltip
            pond={tooltip.pond}
            x={tooltip.screenX}
            y={tooltip.screenY}
            status={tooltip.status}
          />
        )}
      </div>

      {/* Summary */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 bg-gray-50 rounded-xl text-sm text-gray-600">
        <span>
          Total: <span className="font-semibold text-gray-900">{ponds.length}</span>
        </span>
        <span className="text-gray-300">|</span>
        <span>
          Active: <span className="font-semibold text-gray-900">{activePonds.length}</span>
        </span>
        {preparingPonds.length > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <span>
              Preparing: <span className="font-semibold text-gray-900">{preparingPonds.length}</span>
            </span>
          </>
        )}
        <span className="text-gray-300">|</span>
        <span>
          Idle: <span className="font-semibold text-gray-900">{idlePonds.length}</span>
        </span>
        {alertCount > 0 && (
          <>
            <span className="text-gray-300">|</span>
            <span className="text-red-600">
              Alerts: <span className="font-semibold">{alertCount}</span>
            </span>
          </>
        )}
        {hasSavedDefault && (
          <>
            <span className="text-gray-300">|</span>
            <span className="text-gray-400 text-xs">Custom layout saved</span>
          </>
        )}
      </div>
    </div>
  );
}

function VegetationPatches({ vw, vh }: { vw: number; vh: number }) {
  const patches = [
    { cx: 35, cy: 45, rx: 18, ry: 12 },
    { cx: vw - 40, cy: 50, rx: 22, ry: 14 },
    { cx: 30, cy: vh - 45, rx: 20, ry: 13 },
    { cx: vw - 35, cy: vh - 40, rx: 16, ry: 11 },
    { cx: vw / 2 - 60, cy: 35, rx: 25, ry: 10 },
    { cx: vw / 2 + 80, cy: vh - 35, rx: 22, ry: 12 },
    { cx: 35, cy: vh / 2 + 30, rx: 14, ry: 18 },
    { cx: vw - 30, cy: vh / 2 - 20, rx: 16, ry: 20 },
  ];

  return (
    <g>
      {patches.map((p, i) => (
        <ellipse
          key={i}
          cx={p.cx} cy={p.cy} rx={p.rx} ry={p.ry}
          fill={i % 2 === 0 ? '#4A6B3A' : '#3D5C30'}
          opacity={0.6}
        />
      ))}
    </g>
  );
}

function DikePaths({
  positions,
  ponds,
}: {
  positions: Record<string, { x: number; y: number; w: number; h: number }>;
  ponds: PondOverview[];
}) {
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];

  for (let i = 0; i < ponds.length; i++) {
    const posA = positions[ponds[i].id];
    if (!posA) continue;
    const cxA = posA.x + posA.w / 2;
    const cyA = posA.y + posA.h / 2;

    for (let j = i + 1; j < ponds.length; j++) {
      const posB = positions[ponds[j].id];
      if (!posB) continue;
      const cxB = posB.x + posB.w / 2;
      const cyB = posB.y + posB.h / 2;

      const dx = Math.abs(cxA - cxB);
      const dy = Math.abs(cyA - cyB);
      const avgW = (posA.w + posB.w) / 2;
      const avgH = (posA.h + posB.h) / 2;

      const isHNeighbor = dy < avgH * 0.6 && dx < avgW * 2.5 && dx > avgW * 0.3;
      const isVNeighbor = dx < avgW * 0.8 && dy < avgH * 2.5 && dy > avgH * 0.3;

      if (isHNeighbor || isVNeighbor) {
        lines.push({ x1: cxA, y1: cyA, x2: cxB, y2: cyB });
      }
    }
  }

  return (
    <g>
      {lines.map((line, i) => (
        <line
          key={i}
          x1={line.x1} y1={line.y1}
          x2={line.x2} y2={line.y2}
          stroke="url(#dike-fill)"
          strokeWidth="22"
          strokeLinecap="round"
          opacity="0.4"
        />
      ))}
    </g>
  );
}
